<template>
  <div>
    <button
      class="ag-link ag-font08 border-0 mb-2 p-0"
      v-if="$route.meta.back"
      type="button"
      @click="$router.go(-1)"
    >
      <small>{{ t("backtopage", [t($route.meta.title)]) }}</small>
    </button>
    <ag-iframe :loading="loading" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { useI18n } from "vue-i18n";
import { defineComponent, ref } from "vue";
import AgIframe from "../../components/ag-iframe";
import { useStore } from "@/store";
import { AuthActionTypes } from "@/store/modules/auth/action-types";
import { Roles } from "@/helpers/enums/Roles";
import { GeneralActionTypes } from "@/store/modules/general/action-types";
import api from "@/api";
import { useRouter, useRoute } from "vue-router";

export default defineComponent({
  name: "Iframe",
  components: { AgIframe },
  setup() {
    const { t, locale } = useI18n();

    const store = useStore();
    const router = useRouter();

    const role = localStorage.getItem("selectedRole")
      ? localStorage.getItem("selectedRole")
      : store.getters.loggedInUser.role;
    const isAdmin = [Roles.AGMSAdmin, Roles.PartnerAdmin, Roles.CompanyAdmin].includes(role);
    const isSiteAdmin = [Roles.SiteAdmin].includes(role);

    const loading = ref(true);
    const companyList = "/administration/company-list";

    const setAgmsBreadcrumbStatus = function (status) {
      store.dispatch(GeneralActionTypes.SET_AGMS_BREADCRUMB_STATUS, status);
    };

    const clearSiteAndGroup = function () {
      localStorage.setItem("site", JSON.stringify({}));
      localStorage.setItem("group", JSON.stringify({}));
      store.dispatch(AuthActionTypes.SET_SITE, {});
      store.dispatch(AuthActionTypes.SET_GROUP, {});
    };

    const changeRole = function (role) {
      let tempUser = store.getters.mainUser;
      const roleIndex = tempUser.findIndex((i) => i.type === "role");
      localStorage.setItem("scopeChange", true);
      localStorage.setItem("selectedRole", role);
      localStorage.setItem('changingScope', true);
      if (roleIndex > -1) {
        tempUser[roleIndex].value = role;
        store.dispatch(AuthActionTypes.SET_USER, tempUser);

        return true;
      }
      return false;
    };

    const changeCompany = function (company) {
      if (changeRole(Roles.CompanyAdmin)) {
        store.dispatch(AuthActionTypes.SET_COMPANY, company);
        clearSiteAndGroup();
        localStorage.setItem("company", JSON.stringify(company));
        store.dispatch(
          GeneralActionTypes.SET_COMPKEY,
          store.getters.compKey + 1
        );
      }
    };

    const changeSite = function (site) {
      if (changeRole(Roles.SiteAdmin)) {
        clearSiteAndGroup();
        store.dispatch(AuthActionTypes.SET_SITE, site);
        localStorage.setItem("site", JSON.stringify(site));
        store.dispatch(AuthActionTypes.SET_AGMS_TEMPLATE, {});
        store.dispatch(
          GeneralActionTypes.SET_COMPKEY,
          store.getters.compKey + 1
        );
      }
    };

    const changeGroup = function (site, group, routerState = true) {

      if (changeRole(Roles.GroupAdmin)) {
        clearSiteAndGroup();
        localStorage.setItem("selectedScopeId", group.key)
        localStorage.setItem("site", JSON.stringify(site));
        localStorage.setItem("group", JSON.stringify(group));
        store.dispatch(AuthActionTypes.SET_SITE, site);
        store.dispatch(AuthActionTypes.SET_GROUP, group);
        store.dispatch(AuthActionTypes.SET_AGMS_TEMPLATE, {});
        store.dispatch(
          GeneralActionTypes.SET_COMPKEY,
          store.getters.compKey + 1
        );
        if(routerState){
          router.push('/administration/group-dashboard');
        }
      }
    };

    window.addEventListener(
      "message",
      function (event) {
        console.log("postmessage: " + event.data.message);
        if (event.data.message === "sessionExpired") {
          localStorage.clear();
          sessionStorage.clear();
          window.location.href = "/bff/logout?sid=" + (store.getters.loggedInUser ? store.getters.loggedInUser.sid : '');
        
        }

        if (event.data.message == "onSelectGroup") {
          
          const data = event.data.value;
          data.name = event.data.value.group;
          store.dispatch(AuthActionTypes.SET_GROUP, data);

          api.sites.getByMainId(data.siteId).then((res) => {
            api.groups
              .getSiteGroups(res.data.entityData.siteGuid)
              .then((resGroup) => {  
                const selectedGroup = resGroup.data.entityData.find(
                  (i) => i.name === data.group
                );
                changeGroup(
                  {
                    key: res.data.entityData.siteGuid,
                    value: res.data.entityData.name,
                    mainid: data.siteId,
                  },
                  {
                    key: selectedGroup ? selectedGroup.groupGuid : null,
                    value: data.group,
                    mainid: data.mainid,
                    siteId: res.data.entityData.siteGuid
                  }
                );
              });
          });

          if (isAdmin) {
            store.dispatch(AuthActionTypes.SET_AGMS_TEMPLATE, {});
            store.dispatch(AuthActionTypes.SET_SITE, {});
            api.sites.getByMainId(data.siteId).then((res) => {
              store.dispatch(GeneralActionTypes.SET_AGMS_BREADCRUMB_DATA, {
                group: data,
                site: {
                  site: res.data.entityData.name,
                  siteGuid: res.data.entityData.siteGuid,
                },
              });
            });

            setAgmsBreadcrumbStatus(true);
          }

          if (isSiteAdmin) {
            api.sites.getByMainId(data.siteId).then((res) => {
              store.dispatch(GeneralActionTypes.SET_AGMS_BREADCRUMB_DATA, {
                group: data,
                site: {
                  site: res.data.entityData.name,
                  siteGuid: res.data.entityData.siteGuid,
                },
              });
            });

            setAgmsBreadcrumbStatus(true);
          }
        }
        if (event.data.message == "onSelectSite") {
          const data = event.data.value;
          data.name = event.data.value.site;
          store.dispatch(AuthActionTypes.SET_SITE, data);

          api.sites.getByMainId(data.mainid).then((res) => {
            changeSite({
              key: res.data.entityData.siteGuid,
              value: data.name,
              mainid: data.mainid,
            });
          });

          if (isAdmin) {
            store.dispatch(AuthActionTypes.SET_GROUP, {});
            store.dispatch(GeneralActionTypes.SET_AGMS_BREADCRUMB_DATA, {
              site: data,
              group: {},
            });
            setAgmsBreadcrumbStatus(true);
          }
        }
        if (event.data.message == "onSelectCompany") {
          store.dispatch(AuthActionTypes.SET_COMPANY, event.data.value);
          changeCompany(event.data.value);
          if (isAdmin) {
            store.dispatch(AuthActionTypes.SET_SITE, {});
            store.dispatch(AuthActionTypes.SET_GROUP, {});
            setAgmsBreadcrumbStatus(false);
          }
        }
        if (event.data.message == "onSelectTemplate") {
          const agmsGroupStr = localStorage.getItem("agms_group");
          const hasGroup = (() => {
            try {
              const parsed = agmsGroupStr ? JSON.parse(agmsGroupStr) : null;
              return parsed && parsed.mainid;
            } catch {
              return false;
            }
          })();

          store.dispatch(
            AuthActionTypes.SET_AGMS_TEMPLATE,
            hasGroup ? {} : event.data.value
          );
          // this.$router.reload();
        }
        if (event.data.message == "onPageLoad") {
          loading.value = false;
        }
        if (event.data.message == "onTemplatePolicySelected") {
          const partner = JSON.parse(localStorage.getItem("partner"));
          const company = localStorage.getItem("company");
          const companyGuid = company ? JSON.parse(company)?.companyGuid : "";
          const selectedCompany = partner.nodes.find((i) => i.guid === companyGuid);
          const selectedSite = selectedCompany.nodes.find((i) => i.name === event.data.value.siteName);
          const siteId= selectedSite.mainId;

          localStorage.setItem("selectedRole", Roles.GroupAdmin);
          localStorage.setItem("agms_template", JSON.stringify({}));        

          const data = event.data.value || {};

          api.sites.getByMainId(siteId).then((res) => {
            api.groups
              .getSiteGroups(res.data.entityData.siteGuid)
              .then((resGroup) => {
                const selectedGroup = resGroup.data.entityData.find(
                  (i) => i.name === data.groupName
                );
                changeGroup(
                  {
                    key: res.data.entityData.siteGuid,
                    value: res.data.entityData.name,
                    mainid: data.siteId,
                  },
                  {
                    key: selectedGroup ? selectedGroup.groupGuid : null,
                    value: data.groupName,
                    mainid: data.mainid,
                    siteId: res.data.entityData.siteGuid
                  }, false
                );
                store.dispatch(GeneralActionTypes.SET_AGMS_BREADCRUMB_DATA, {
                  group: { group: data.groupName, mainid: data.mainid },
                  site: {
                    site: res.data.entityData.name,
                    siteGuid: res.data.entityData.siteGuid,
                  },
                });
                setAgmsBreadcrumbStatus(true);
              });
          });
          localStorage.setItem("agms_group", JSON.stringify({ "mainid": event.data.value.mainid, "group": event.data.value.groupName }));

          store.dispatch(AuthActionTypes.SET_AGMS_SITE, {
            site : event.data.value.siteName
          });

          if(event.data.value.policyTypeID == 19)
            router.push('/administration/ignore-events');
          else if (event.data.value.policyTypeID == 20)
            router.push('/administration/severity-events');
          else if (event.data.value.policyTypeID == 7){
            router.push('/administration/app-group-management/detail?id=' + event.data.value.policyId);
            localStorage.setItem("group",JSON.stringify({ "value": event.data.value.groupName, "mainid": event.data.value.mainid }));
          }
          else if (event.data.value.policyTypeID == 12){
            localStorage.setItem("group",JSON.stringify({ "value": event.data.value.groupName, "mainid": event.data.value.mainid }));
            router.push('/administration/global-policy-management');
          }
          else if (event.data.value.policyTypeID == 17){
            localStorage.setItem("group",JSON.stringify({ "value": event.data.value.groupName, "mainid": event.data.value.mainid }));
            router.push('/administration/file-types-policy');
          }
        }
        if (event.data.message == "newCompany") {
          localStorage.setItem("manageCompany", JSON.stringify({ "name": event.data.message }));
          router.push(companyList);
        }
        if (event.data.message == "deleteCompany" || event.data.message == "editCompany") {
          localStorage.setItem("manageCompany", JSON.stringify({ "name": event.data.message,"companyGuid": event.data.value.companyId }));
          router.push(companyList);
        }
        if (event.data.message == "companyInfo") {
          router.push(companyList);
        }
      },
      false
    );

    return {
      t,
      locale,
      loading,
    };
  },
});
</script>

<style scoped></style>
